/**
 * Copyright by:
 * by ESS-Ehrhardt Software Solutions & Service GmbH
 * Basselscheider Str. 55
 * D-56281 Emmelshausen
 * +49 (0) 67 42 / 87 27 0
 * info@epg.com
 */

import { Component, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';

@Component({
  selector: 'app-animated-counter',
  template: `<span>{{ displayValue }}</span>`,
  styleUrls: ['./animated-counter.component.scss'],
  standalone: false
})
export class AnimatedCounterComponent implements OnChanges, OnDestroy {
  @Input() value: number = 0;
  @Input() duration: number = 800;
  @Input() delay: number = 0;

  displayValue: number = 0;
  private animationId: number | null = null;
  private timeoutId: number | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && changes['value'].currentValue !== changes['value'].previousValue) {
      this.animateToValue(this.value);
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private animateToValue(targetValue: number): void {
    this.cleanup();

    const startValue = this.displayValue;
    const difference = targetValue - startValue;

    if (difference === 0) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      this.displayValue = targetValue;
      return;
    }

    const startAnimation = () => {
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / this.duration, 1);

        const easeOut = 1 - Math.pow(1 - progress, 3);

        this.displayValue = Math.round(startValue + difference * easeOut);

        if (progress < 1) {
          this.animationId = requestAnimationFrame(animate);
        } else {
          this.displayValue = targetValue;
          this.animationId = null;
        }
      };

      this.animationId = requestAnimationFrame(animate);
    };

    if (this.delay > 0) {
      this.timeoutId = window.setTimeout(startAnimation, this.delay);
    } else {
      startAnimation();
    }
  }

  private cleanup(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}
